import type { Card, GameState, HandState } from '../game/types';
import {
  getEnemyAiCombatHistory,
  getEnemyAiCurrentTurnActions,
  titleOfEnemyAiCard,
  trailingSameCount,
} from './generic';
import type { EnemyAiDecision, EnemyAiDecisionContext } from './types';

const TITLES = {
  thresholdCheck: 'threshold check',
  punitiveMeasure: 'punitive measure',
  unauthorizedPassage: 'unauthorized passage',
  counterweight: 'counterweight',
  deferredSanction: 'deferred sanction',
} as const;

const HARD_NO = -100000;

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function clamp01(value: number): number {
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

function handCount(hand: HandState | null | undefined): number {
  if (!hand) return 0;
  let count = 0;
  if (hand.main?.physical) count += 1;
  if (hand.main?.magic) count += 1;
  count += Array.isArray(hand.extra) ? hand.extra.length : 0;
  return count;
}

function getUnauthorizedLockCount(state: GameState): number {
  const raw = Array.isArray((state as any).unauthorizedPassageLocks)
    ? ((state as any).unauthorizedPassageLocks as Array<any>)
    : [];
  return raw.filter((entry) => {
    const cardId = String(entry?.cardId ?? '').trim();
    const turns = Math.max(0, Math.floor(Number(entry?.turns ?? 0) || 0));
    return cardId.length > 0 && turns > 0;
  }).length;
}

function isPunitiveMeasureActive(state: GameState): boolean {
  return Math.max(0, Math.floor(Number((state as any).punitiveMeasureTurns ?? 0) || 0)) > 0;
}

function totalUses(history: string[], title: string): number {
  const wanted = norm(title);
  return history.reduce((count, entry) => count + (norm(entry) === wanted ? 1 : 0), 0);
}

function usesInRecentTurns(state: GameState, title: string, turns: number): number {
  const wanted = norm(title);
  const history = getEnemyAiCombatHistory(state);
  const current = getEnemyAiCurrentTurnActions(state);
  const recent = [...history.slice(-Math.max(0, turns)), ...current];
  return recent.filter((entry) => norm(entry) === wanted).length;
}

function deferredSanctionUsesInRecentTurns(state: GameState): number {
  return usesInRecentTurns(state, TITLES.deferredSanction, 5);
}

function getPendingDeferredSanctionBundles(state: GameState): number {
  const delayed = Array.isArray((state as any).delayed) ? ((state as any).delayed as Array<any>) : [];
  return delayed.filter((bundle) => {
    const sourceTitle = norm(bundle?.sourceTitle);
    const owner = norm(bundle?.owner ?? 'player');
    const turnsLeft = Math.max(0, Math.floor(Number(bundle?.turnsLeft ?? 0) || 0));
    return sourceTitle === TITLES.deferredSanction && owner === 'enemy' && turnsLeft > 0;
  }).length;
}

function estimateUnauthorizedGapTurns(state: GameState): number {
  const lockCount = getUnauthorizedLockCount(state);
  if (lockCount > 0) return 0;

  const history = getEnemyAiCombatHistory(state);
  let lastUnauthorizedIndex = -1;
  for (let i = history.length - 1; i >= 0; i -= 1) {
    if (norm(history[i]) === TITLES.unauthorizedPassage) {
      lastUnauthorizedIndex = i;
      break;
    }
  }

  if (lastUnauthorizedIndex < 0) {
    return history.length;
  }

  const enemyActionsSinceUnauthorized = Math.max(0, history.length - 1 - lastUnauthorizedIndex);
  return Math.max(0, enemyActionsSinceUnauthorized - 2);
}

function seedFromState(state: GameState, salt: string): number {
  let seed = Math.imul((Number((state as any).turn ?? 0) || 0) + 1, 1103515245) >>> 0;
  seed = (seed ^ Math.imul((Number(state.player?.hp ?? 0) || 0) + 3, 2654435761)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.player?.sp ?? 0) || 0) + 5, 2246822519)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.enemy?.hp ?? 0) || 0) + 7, 3266489917)) >>> 0;
  seed = (seed ^ Math.imul((Number(state.enemy?.armor ?? 0) || 0) + 11, 668265263)) >>> 0;
  const history = getEnemyAiCombatHistory(state).slice(-8).join('|');
  const text = `${salt}|${String(state.enemyId ?? '')}|${history}`;
  for (let i = 0; i < text.length; i += 1) {
    seed = (seed ^ text.charCodeAt(i)) >>> 0;
    seed = Math.imul(seed, 16777619) >>> 0;
  }
  return seed >>> 0;
}

function deterministicRoll01(state: GameState, salt: string): number {
  return (seedFromState(state, salt) % 10000) / 10000;
}

function counterweightChance(lastPlayerTurnPlays: number): number {
  if (lastPlayerTurnPlays < 3) return 0;
  if (lastPlayerTurnPlays === 3) return 0.2;
  if (lastPlayerTurnPlays === 4) return 0.6;
  if (lastPlayerTurnPlays === 5) return 0.8;
  if (lastPlayerTurnPlays === 6) return 0.9;
  return 1;
}

function chooseBest(scored: Array<{ card: Card; score: number; reason: string }>): Card | null {
  if (!scored.length) return null;
  const sorted = [...scored].sort((a, b) => {
    if (a.score !== b.score) return b.score - a.score;
    return titleOfEnemyAiCard(a.card).localeCompare(titleOfEnemyAiCard(b.card));
  });
  return sorted[0]?.card ?? null;
}

function scoreCard(card: Card, context: EnemyAiDecisionContext): { score: number; reason: string } {
  const { state } = context;
  const title = titleOfEnemyAiCard(card);
  const history = getEnemyAiCombatHistory(state);
  const playerHandCount = handCount(state.hand);
  const lockCount = getUnauthorizedLockCount(state);
  const punitiveActive = isPunitiveMeasureActive(state);
  const lastPlayerTurnPlays = Math.max(0, Math.floor(Number((state as any).lastPlayerTurnPlays ?? 0) || 0));
  const pendingDeferredBundles = getPendingDeferredSanctionBundles(state);
  const recentUses = totalUses(history, title);
  const sameTrail = trailingSameCount(history, title);
  const unauthorizedGapTurns = estimateUnauthorizedGapTurns(state);

  let score = 0;
  let reason = 'fallback';

  if (title === TITLES.thresholdCheck) {
    score = 5200;
    reason = 'baseline_threshold_check';
    if (playerHandCount > 0 && lockCount === 0 && unauthorizedGapTurns > 1) {
      score -= 1600;
      reason = 'prefer_reapply_lock_after_gap';
    }
    if (playerHandCount >= 4 && !punitiveActive) {
      score -= 700;
    }
    if (lastPlayerTurnPlays >= 3) {
      score -= 900;
    }
  } else if (title === TITLES.unauthorizedPassage) {
    if (playerHandCount <= 0) {
      return { score: HARD_NO, reason: 'no_cards_in_hand_to_lock' };
    }
    if (lockCount > 0) {
      return { score: HARD_NO, reason: 'unauthorized_already_active' };
    }
    if (unauthorizedGapTurns > 1) {
      score = 9400;
      reason = 'reapply_lock_after_gap';
    } else {
      score = 3300;
      reason = 'lock_optional_recent_gap';
    }
  } else if (title === TITLES.counterweight) {
    if (lastPlayerTurnPlays < 3) {
      return { score: HARD_NO, reason: 'needs_three_player_cards_last_turn' };
    }
    if (usesInRecentTurns(state, TITLES.counterweight, 3) > 0) {
      return { score: HARD_NO, reason: 'counterweight_cooldown_3' };
    }
    const chance01 = counterweightChance(lastPlayerTurnPlays);
    const roll01 = deterministicRoll01(state, `${TITLES.counterweight}|${lastPlayerTurnPlays}`);
    if (roll01 >= chance01) {
      return { score: HARD_NO, reason: `counterweight_roll_failed_${chance01.toFixed(3)}` };
    }
    score = 7400 + Math.min(2000, Math.max(0, lastPlayerTurnPlays - 3) * 650);
    reason = `counterweight_roll_passed_${chance01.toFixed(3)}`;
  } else if (title === TITLES.punitiveMeasure) {
    if (punitiveActive) {
      return { score: HARD_NO, reason: 'punitive_already_active' };
    }
    if (usesInRecentTurns(state, TITLES.punitiveMeasure, 2) > 0) {
      return { score: HARD_NO, reason: 'punitive_measure_cooldown_2' };
    }
    if (playerHandCount < 4) {
      return { score: HARD_NO, reason: 'player_not_ending_with_four_cards' };
    }
    score = 7700 + Math.max(0, (playerHandCount - 4) * 250);
    reason = 'punish_large_hand';
  } else if (title === TITLES.deferredSanction) {
    if (deferredSanctionUsesInRecentTurns(state) > 0) {
      return { score: HARD_NO, reason: 'deferred_sanction_cooldown_5' };
    }
    score = 2400;
    reason = 'periodic_delayed_pressure';
    if (pendingDeferredBundles > 0) score -= 500;
  } else {
    score = 1000;
    reason = 'unrecognized_threshold_card';
  }

  if (sameTrail >= 2) {
    if (title === TITLES.thresholdCheck) {
      score -= 3600;
      reason = `${reason}_avoid_third_consecutive_when_possible`;
    } else {
      return { score: HARD_NO, reason: `${reason}_avoid_third_consecutive` };
    }
  } else if (sameTrail === 1) {
    score -= 850;
  }

  score -= recentUses * (title === TITLES.thresholdCheck ? 40 : 55);

  return { score, reason };
}

export function pickThresholdVerifierCard(context: EnemyAiDecisionContext): EnemyAiDecision {
  const { availableCards } = context;
  if (!Array.isArray(availableCards) || availableCards.length === 0) {
    return { card: null };
  }

  const scored = availableCards.map((card) => {
    const result = scoreCard(card, context);
    return {
      card,
      score: result.score,
      reason: result.reason,
    };
  });

  const best = chooseBest(scored);
  const bestEntry = scored.find((entry) => entry.card === best) ?? scored[0];

  return {
    card: best,
    reason: bestEntry?.reason ?? 'threshold_verifier_fallback',
    debug: scored
      .map((entry) => ({
        title: entry.card.title,
        score: entry.score,
        reason: entry.reason,
      }))
      .sort((a, b) => b.score - a.score),
  };
}
